import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    FormControlLabel,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { DesktopLayout } from 'src/layout';
import CrmIntegrationsService, {
    CrmConnection,
    CrmConnectionPayload,
    CrmProvider,
} from 'src/services/CrmIntegrationsService';

const BORDER = '#E3ECF3';
const CARD_RADIUS = '8px';

const CRM_PROVIDERS: Array<{
    id: CrmProvider;
    label: string;
    description: string;
    primaryFieldLabel: string;
    primaryFieldPlaceholder: string;
}> = [
    {
        id: 'amocrm',
        label: 'AmoCRM',
        description: 'Контакты, сделки и этапы воронки',
        primaryFieldLabel: 'Адрес аккаунта',
        primaryFieldPlaceholder: 'company.amocrm.ru',
    },
    {
        id: 'bitrix24',
        label: 'Bitrix24',
        description: 'Синхронизация через входящий webhook',
        primaryFieldLabel: 'Webhook URL',
        primaryFieldPlaceholder: 'company.bitrix24.ru/rest/1/key',
    },
];

type CrmFormState = {
    name: string;
    baseUrl: string;
    webhookUrl: string;
    clientId: string;
    clientSecret: string;
    redirectUri: string;
    authorizationCode: string;
    accessToken: string;
    refreshToken: string;
    responsibleUserId: string;
    selectedPipelineId: string;
    isActive: boolean;
    syncNewLeads: boolean;
    syncDialogueMessages: boolean;
    syncPipelineStages: boolean;
};

const cardSx = {
    bgcolor: '#fff',
    border: `1px solid ${BORDER}`,
    borderRadius: CARD_RADIUS,
    boxShadow: 'none',
} as const;

const textFieldSx = {
    '& .MuiInputLabel-root': {
        color: '#647991',
        fontWeight: 600,
        zIndex: 1,
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
        bgcolor: '#fff',
        px: 0.5,
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: '#1F1F1F',
    },
    '& .MuiInputLabel-root.Mui-error': {
        color: '#D33B2F',
    },
    '& .MuiOutlinedInput-root': {
        borderRadius: CARD_RADIUS,
        bgcolor: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: BORDER,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#1F1F1F',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#1F1F1F',
    },
} as const;

function createDefaultForm(provider: CrmProvider): CrmFormState {
    const providerInfo = getProviderInfo(provider);

    return {
        name: providerInfo.label,
        baseUrl: '',
        webhookUrl: '',
        clientId: '',
        clientSecret: '',
        redirectUri: '',
        authorizationCode: '',
        accessToken: '',
        refreshToken: '',
        responsibleUserId: '',
        selectedPipelineId: '',
        isActive: true,
        syncNewLeads: true,
        syncDialogueMessages: true,
        syncPipelineStages: true,
    };
}

function getProviderInfo(provider: CrmProvider) {
    return CRM_PROVIDERS.find((item) => item.id === provider) || CRM_PROVIDERS[0];
}

function normalizeUrl(value: string) {
    const raw = value.trim();
    if (!raw) {
        return '';
    }
    if (/^https?:\/\//i.test(raw)) {
        return raw.replace(/\/+$/, '');
    }
    return `https://${raw.replace(/\/+$/, '')}`;
}

function formatDateTime(value?: string) {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function getStatusMeta(connection?: CrmConnection) {
    if (!connection) {
        return { label: 'Не подключено', color: '#647991', bgcolor: '#F1F5F9' };
    }
    if (connection.healthStatus === 'ready') {
        return { label: connection.isActive ? 'Подключено' : 'Отключено', color: '#16813A', bgcolor: '#EAF8EF' };
    }
    if (connection.healthStatus === 'error') {
        return { label: 'Ошибка', color: '#D33B2F', bgcolor: '#FFF0EE' };
    }
    return { label: 'Черновик', color: '#0044FF', bgcolor: '#EEF4FF' };
}

function getBindingStatusLabel(value: string) {
    if (value === 'synced') {
        return 'Синхронизировано';
    }
    if (value === 'error') {
        return 'Ошибка';
    }
    if (value === 'skipped') {
        return 'Пропущено';
    }
    return 'Ожидает';
}

function getApiErrorMessage(error: unknown, fallback: string) {
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    if (!data) {
        return fallback;
    }
    if (typeof data === 'string') {
        return data;
    }
    if (typeof data === 'object') {
        const payload = data as Record<string, unknown>;
        if (typeof payload.detail === 'string') {
            return payload.detail;
        }
        const firstValue = Object.values(payload)[0];
        if (Array.isArray(firstValue)) {
            return String(firstValue[0] || fallback);
        }
        if (firstValue) {
            return String(firstValue);
        }
    }
    return fallback;
}

export default function IntegrationsScreen() {
    const { enqueueSnackbar } = useSnackbar();
    const [connections, setConnections] = useState<CrmConnection[]>([]);
    const [selectedProvider, setSelectedProvider] = useState<CrmProvider>('amocrm');
    const [form, setForm] = useState<CrmFormState>(() => createDefaultForm('amocrm'));
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [bindings, setBindings] = useState<Awaited<ReturnType<typeof CrmIntegrationsService.listBindings>>>([]);
    const [loading, setLoading] = useState(true);
    const [bindingsLoading, setBindingsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [oauthOpen, setOauthOpen] = useState(false);

    const selectedConnection = useMemo(
        () => connections.find((connection) => connection.provider === selectedProvider) || null,
        [connections, selectedProvider]
    );
    const providerInfo = getProviderInfo(selectedProvider);

    const updateConnectionInState = useCallback((connection: CrmConnection) => {
        setConnections((current) =>
            [connection, ...current.filter((item) => item.provider !== connection.provider)].sort((a, b) =>
                a.provider.localeCompare(b.provider)
            )
        );
    }, []);

    const loadBindings = useCallback(async (connectionId: number) => {
        setBindingsLoading(true);
        try {
            const loaded = await CrmIntegrationsService.listBindings(connectionId);
            setBindings(loaded);
        } catch (_error) {
            setBindings([]);
        } finally {
            setBindingsLoading(false);
        }
    }, []);

    const loadConnections = useCallback(async () => {
        setLoading(true);
        try {
            const loaded = await CrmIntegrationsService.listConnections();
            setConnections(loaded);
            if (loaded[0]) {
                setSelectedProvider(loaded[0].provider);
            }
        } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, 'Не удалось загрузить CRM-интеграции'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        void loadConnections();
    }, [loadConnections]);

    useEffect(() => {
        const connection = connections.find((item) => item.provider === selectedProvider);
        if (!connection) {
            setForm(createDefaultForm(selectedProvider));
            setBindings([]);
            setOauthOpen(false);
            return;
        }

        setForm({
            name: connection.name || getProviderInfo(selectedProvider).label,
            baseUrl: connection.baseUrl,
            webhookUrl: connection.webhookUrl,
            clientId: connection.clientId,
            clientSecret: '',
            redirectUri: connection.redirectUri,
            authorizationCode: '',
            accessToken: '',
            refreshToken: '',
            responsibleUserId: connection.responsibleUserId,
            selectedPipelineId: connection.selectedPipelineId,
            isActive: connection.isActive,
            syncNewLeads: connection.syncNewLeads,
            syncDialogueMessages: connection.syncDialogueMessages,
            syncPipelineStages: connection.syncPipelineStages,
        });
        setFieldErrors({});
        setOauthOpen(false);
        void loadBindings(connection.id);
    }, [connections, loadBindings, selectedProvider]);

    const updateForm = (patch: Partial<CrmFormState>) => {
        setForm((current) => ({ ...current, ...patch }));
        setFieldErrors({});
    };

    const buildPayload = (): CrmConnectionPayload | null => {
        const nextErrors: Record<string, string> = {};
        const baseUrl = normalizeUrl(form.baseUrl);
        const webhookUrl = normalizeUrl(form.webhookUrl);

        if (selectedProvider === 'amocrm' && !baseUrl) {
            nextErrors.baseUrl = 'Укажите адрес аккаунта amoCRM';
        }
        if (selectedProvider === 'bitrix24' && !webhookUrl) {
            nextErrors.webhookUrl = 'Укажите webhook URL Bitrix24';
        }

        setFieldErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) {
            enqueueSnackbar(Object.values(nextErrors)[0], { variant: 'info' });
            return null;
        }

        const pipeline = selectedConnection?.pipelineSnapshot.find((item) => item.id === form.selectedPipelineId);
        const payload: CrmConnectionPayload = {
            provider: selectedProvider,
            name: form.name.trim() || providerInfo.label,
            isActive: form.isActive,
            syncNewLeads: form.syncNewLeads,
            syncDialogueMessages: form.syncDialogueMessages,
            syncPipelineStages: form.syncPipelineStages,
            clientId: form.clientId.trim(),
            redirectUri: normalizeUrl(form.redirectUri),
            responsibleUserId: form.responsibleUserId.trim(),
            selectedPipelineId: form.selectedPipelineId,
            selectedPipelineName: pipeline?.name || selectedConnection?.selectedPipelineName || '',
        };

        if (selectedProvider === 'amocrm') {
            payload.baseUrl = baseUrl;
        }
        if (selectedProvider === 'bitrix24') {
            payload.webhookUrl = webhookUrl;
        }

        if (form.clientSecret.trim()) {
            payload.clientSecret = form.clientSecret.trim();
        }
        if (form.authorizationCode.trim()) {
            payload.authorizationCode = form.authorizationCode.trim();
        }
        if (form.accessToken.trim()) {
            payload.accessToken = form.accessToken.trim();
        }
        if (form.refreshToken.trim()) {
            payload.refreshToken = form.refreshToken.trim();
        }

        return payload;
    };

    const saveConnection = async (showToast = true) => {
        const payload = buildPayload();
        if (!payload) {
            return null;
        }

        setSaving(true);
        try {
            const saved = await CrmIntegrationsService.saveConnection(payload);
            updateConnectionInState(saved);
            setForm((current) => ({
                ...current,
                clientSecret: '',
                authorizationCode: '',
                accessToken: '',
                refreshToken: '',
            }));
            if (showToast) {
                enqueueSnackbar('Интеграция сохранена', { variant: 'success' });
            }
            return saved;
        } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, 'Не удалось сохранить интеграцию'), { variant: 'error' });
            return null;
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        const connection = selectedConnection || (await saveConnection(false));
        if (!connection) {
            return;
        }

        setTesting(true);
        try {
            const result = await CrmIntegrationsService.testConnection(connection.id);
            updateConnectionInState(result.connection);
            setForm((current) => ({
                ...current,
                selectedPipelineId: result.connection.selectedPipelineId,
            }));
            await loadBindings(result.connection.id);
            enqueueSnackbar('Подключение проверено', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, 'CRM не ответила на проверку'), { variant: 'error' });
        } finally {
            setTesting(false);
        }
    };

    const handleSyncNow = async () => {
        if (!selectedConnection) {
            return;
        }

        setSyncing(true);
        try {
            const result = await CrmIntegrationsService.syncNow(selectedConnection.id);
            updateConnectionInState(result.connection);
            await loadBindings(result.connection.id);
            enqueueSnackbar(`Синхронизировано: ${result.summary.bindings}`, { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, 'Не удалось запустить синхронизацию'), { variant: 'error' });
        } finally {
            setSyncing(false);
        }
    };

    return (
        <DesktopLayout hideRightSidebar>
            <Stack
                spacing={2.5}
                minHeight="100%"
                sx={{ minWidth: 0, overflowX: 'hidden' }}
            >
                <Box
                    display="flex"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    gap={2}
                    flexDirection={{ xs: 'column', md: 'row' }}
                >
                    <Box>
                        <Typography variant="h1">Интеграции</Typography>
                        <Typography
                            variant="body1"
                            sx={{ color: 'rgba(0,0,0,0.62)', mt: 1 }}
                        >
                            CRM-подключения для лидов
                        </Typography>
                    </Box>
                </Box>

                {loading ? (
                    <Box
                        minHeight={360}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', lg: '340px minmax(0, 1fr)' }}
                        gap={2}
                        alignItems="start"
                        sx={{ minWidth: 0 }}
                    >
                        <Stack spacing={1.25}>
                            {CRM_PROVIDERS.map((provider) => {
                                const connection = connections.find((item) => item.provider === provider.id);
                                const selected = selectedProvider === provider.id;
                                const status = getStatusMeta(connection);
                                return (
                                    <Box
                                        key={provider.id}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setSelectedProvider(provider.id)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                setSelectedProvider(provider.id);
                                            }
                                        }}
                                        sx={{
                                            ...cardSx,
                                            cursor: 'pointer',
                                            p: 1.5,
                                            borderColor: selected ? '#0044FF' : BORDER,
                                            bgcolor: selected ? '#F3F7FF' : '#fff',
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="flex-start"
                                            justifyContent="space-between"
                                            gap={1.25}
                                        >
                                            <Box
                                                width={44}
                                                height={44}
                                                borderRadius={CARD_RADIUS}
                                                bgcolor="#F1F5F9"
                                                display="grid"
                                                flexShrink={0}
                                                fontSize={18}
                                                fontWeight={900}
                                                sx={{ placeItems: 'center' }}
                                            >
                                                {provider.label.slice(0, 1)}
                                            </Box>
                                            <Box
                                                minWidth={0}
                                                flex={1}
                                            >
                                                <Typography sx={{ fontWeight: 900, lineHeight: 1.25 }}>
                                                    {provider.label}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: 'rgba(0,0,0,0.52)',
                                                        mt: 0.35,
                                                        fontSize: 14,
                                                        lineHeight: 1.35,
                                                    }}
                                                >
                                                    {provider.description}
                                                </Typography>
                                            </Box>
                                            {selected && (
                                                <Typography
                                                    aria-hidden
                                                    sx={{ color: '#0044FF', fontSize: 18, fontWeight: 900 }}
                                                >
                                                    ✓
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            mt={1.25}
                                            flexWrap="wrap"
                                        >
                                            <Chip
                                                size="small"
                                                label={status.label}
                                                sx={{
                                                    bgcolor: status.bgcolor,
                                                    color: status.color,
                                                    fontWeight: 800,
                                                }}
                                            />
                                            {formatDateTime(connection?.updatedAt) && (
                                                <Typography sx={{ color: 'rgba(0,0,0,0.46)', fontSize: 13 }}>
                                                    {formatDateTime(connection?.updatedAt)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>

                        <Paper sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, minWidth: 0 }}>
                            <Stack spacing={2.25}>
                                <Box
                                    display="flex"
                                    alignItems={{ xs: 'flex-start', md: 'center' }}
                                    justifyContent="space-between"
                                    gap={1.5}
                                    flexDirection={{ xs: 'column', md: 'row' }}
                                >
                                    <Box>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            flexWrap="wrap"
                                        >
                                            <Typography sx={{ fontSize: 24, fontWeight: 900 }}>
                                                {providerInfo.label}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={getStatusMeta(selectedConnection || undefined).label}
                                                sx={{
                                                    bgcolor: getStatusMeta(selectedConnection || undefined).bgcolor,
                                                    color: getStatusMeta(selectedConnection || undefined).color,
                                                    fontWeight: 800,
                                                }}
                                            />
                                        </Box>
                                        <Typography sx={{ color: 'rgba(0,0,0,0.52)', mt: 0.35 }}>
                                            {providerInfo.description}
                                        </Typography>
                                    </Box>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1}
                                        flexWrap="wrap"
                                        justifyContent={{ xs: 'stretch', md: 'flex-end' }}
                                        width={{ xs: '100%', md: 'auto' }}
                                        maxWidth="100%"
                                        sx={{
                                            rowGap: 1,
                                            '& > button': {
                                                width: { xs: '100%', sm: 'auto' },
                                                minWidth: 0,
                                                flexShrink: 0,
                                            },
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            startIcon={
                                                testing ? (
                                                    <CircularProgress
                                                        size={16}
                                                        color="inherit"
                                                    />
                                                ) : null
                                            }
                                            disabled={saving || testing || syncing}
                                            onClick={handleTest}
                                            sx={{ borderRadius: CARD_RADIUS, textTransform: 'none', fontWeight: 900 }}
                                        >
                                            Проверить
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={
                                                syncing ? (
                                                    <CircularProgress
                                                        size={16}
                                                        color="inherit"
                                                    />
                                                ) : null
                                            }
                                            disabled={!selectedConnection || saving || testing || syncing}
                                            onClick={handleSyncNow}
                                            sx={{ borderRadius: CARD_RADIUS, textTransform: 'none', fontWeight: 900 }}
                                        >
                                            Синхронизировать
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={
                                                saving ? (
                                                    <CircularProgress
                                                        size={16}
                                                        color="inherit"
                                                    />
                                                ) : null
                                            }
                                            disabled={saving || testing || syncing}
                                            onClick={() => void saveConnection()}
                                            sx={{ borderRadius: CARD_RADIUS, textTransform: 'none', fontWeight: 900 }}
                                        >
                                            Сохранить
                                        </Button>
                                    </Stack>
                                </Box>

                                {selectedConnection?.lastError && (
                                    <Box
                                        sx={{
                                            border: '1px solid #FFD6D0',
                                            borderRadius: CARD_RADIUS,
                                            bgcolor: '#FFF7F5',
                                            color: '#B42318',
                                            p: 1.5,
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 850 }}>Последняя ошибка</Typography>
                                        <Typography sx={{ mt: 0.5 }}>{selectedConnection.lastError}</Typography>
                                    </Box>
                                )}

                                <Box
                                    display="grid"
                                    gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                                    gap={1.5}
                                >
                                    <TextField
                                        label="Название подключения"
                                        value={form.name}
                                        onChange={(event) => updateForm({ name: event.target.value })}
                                        sx={textFieldSx}
                                        fullWidth
                                        autoComplete="off"
                                    />
                                    <TextField
                                        label={providerInfo.primaryFieldLabel}
                                        value={selectedProvider === 'amocrm' ? form.baseUrl : form.webhookUrl}
                                        onChange={(event) =>
                                            updateForm(
                                                selectedProvider === 'amocrm'
                                                    ? { baseUrl: event.target.value }
                                                    : { webhookUrl: event.target.value }
                                            )
                                        }
                                        error={Boolean(
                                            selectedProvider === 'amocrm' ? fieldErrors.baseUrl : fieldErrors.webhookUrl
                                        )}
                                        helperText={
                                            selectedProvider === 'amocrm'
                                                ? fieldErrors.baseUrl || 'Можно без https://'
                                                : fieldErrors.webhookUrl || 'Входящий webhook из Bitrix24'
                                        }
                                        placeholder={providerInfo.primaryFieldPlaceholder}
                                        sx={textFieldSx}
                                        fullWidth
                                        autoComplete="off"
                                    />
                                </Box>

                                {selectedProvider === 'amocrm' && (
                                    <Stack spacing={1.25}>
                                        <Box
                                            display="grid"
                                            gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                                            gap={1.5}
                                        >
                                            <TextField
                                                label={
                                                    selectedConnection?.hasAccessToken
                                                        ? 'Новый access token'
                                                        : 'Access token'
                                                }
                                                type="password"
                                                value={form.accessToken}
                                                onChange={(event) => updateForm({ accessToken: event.target.value })}
                                                helperText={
                                                    selectedConnection?.hasAccessToken
                                                        ? 'Токен уже сохранен. Заполните только для замены'
                                                        : 'Можно указать вместо OAuth'
                                                }
                                                sx={textFieldSx}
                                                fullWidth
                                                autoComplete="new-password"
                                            />
                                            <TextField
                                                label="Ответственный в CRM"
                                                value={form.responsibleUserId}
                                                onChange={(event) =>
                                                    updateForm({ responsibleUserId: event.target.value })
                                                }
                                                helperText="ID пользователя, если нужно назначать сделки"
                                                sx={textFieldSx}
                                                fullWidth
                                                autoComplete="off"
                                            />
                                        </Box>
                                        <Button
                                            variant="text"
                                            onClick={() => setOauthOpen((current) => !current)}
                                            sx={{
                                                alignSelf: 'flex-start',
                                                color: '#1F1F1F',
                                                fontWeight: 850,
                                                textTransform: 'none',
                                            }}
                                        >
                                            {oauthOpen ? 'Скрыть OAuth' : 'OAuth-поля'}
                                        </Button>
                                        <Collapse in={oauthOpen}>
                                            <Box
                                                display="grid"
                                                gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                                                gap={1.5}
                                            >
                                                <TextField
                                                    label="Client ID"
                                                    value={form.clientId}
                                                    onChange={(event) => updateForm({ clientId: event.target.value })}
                                                    sx={textFieldSx}
                                                    fullWidth
                                                    autoComplete="off"
                                                />
                                                <TextField
                                                    label={
                                                        selectedConnection?.hasClientSecret
                                                            ? 'Новый Client Secret'
                                                            : 'Client Secret'
                                                    }
                                                    type="password"
                                                    value={form.clientSecret}
                                                    onChange={(event) =>
                                                        updateForm({ clientSecret: event.target.value })
                                                    }
                                                    sx={textFieldSx}
                                                    fullWidth
                                                    autoComplete="new-password"
                                                />
                                                <TextField
                                                    label="Redirect URI"
                                                    value={form.redirectUri}
                                                    onChange={(event) =>
                                                        updateForm({ redirectUri: event.target.value })
                                                    }
                                                    sx={textFieldSx}
                                                    fullWidth
                                                    autoComplete="off"
                                                />
                                                <TextField
                                                    label="Authorization code"
                                                    type="password"
                                                    value={form.authorizationCode}
                                                    onChange={(event) =>
                                                        updateForm({ authorizationCode: event.target.value })
                                                    }
                                                    sx={textFieldSx}
                                                    fullWidth
                                                    autoComplete="new-password"
                                                />
                                                <TextField
                                                    label={
                                                        selectedConnection?.hasRefreshToken
                                                            ? 'Новый refresh token'
                                                            : 'Refresh token'
                                                    }
                                                    type="password"
                                                    value={form.refreshToken}
                                                    onChange={(event) =>
                                                        updateForm({ refreshToken: event.target.value })
                                                    }
                                                    sx={textFieldSx}
                                                    fullWidth
                                                    autoComplete="new-password"
                                                />
                                            </Box>
                                        </Collapse>
                                    </Stack>
                                )}

                                {selectedProvider === 'bitrix24' && (
                                    <TextField
                                        label="Ответственный в CRM"
                                        value={form.responsibleUserId}
                                        onChange={(event) => updateForm({ responsibleUserId: event.target.value })}
                                        helperText="ID пользователя, если нужно назначать сделки"
                                        sx={textFieldSx}
                                        fullWidth
                                        autoComplete="off"
                                    />
                                )}

                                {selectedConnection?.pipelineSnapshot.length ? (
                                    <TextField
                                        select
                                        label="Воронка"
                                        value={form.selectedPipelineId}
                                        onChange={(event) => updateForm({ selectedPipelineId: event.target.value })}
                                        sx={textFieldSx}
                                        fullWidth
                                    >
                                        {selectedConnection.pipelineSnapshot.map((pipeline) => (
                                            <MenuItem
                                                key={pipeline.id}
                                                value={pipeline.id}
                                            >
                                                {pipeline.name || `Воронка ${pipeline.id}`}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                ) : null}

                                <Divider />

                                <Box>
                                    <Typography sx={{ fontWeight: 900, mb: 1 }}>Что синхронизировать</Typography>
                                    <Stack
                                        direction={{ xs: 'column', md: 'row' }}
                                        spacing={{ xs: 0.25, md: 2 }}
                                        flexWrap="wrap"
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.isActive}
                                                    onChange={(event) => updateForm({ isActive: event.target.checked })}
                                                />
                                            }
                                            label="Интеграция активна"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.syncNewLeads}
                                                    onChange={(event) =>
                                                        updateForm({ syncNewLeads: event.target.checked })
                                                    }
                                                />
                                            }
                                            label="Новые лиды"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.syncDialogueMessages}
                                                    onChange={(event) =>
                                                        updateForm({ syncDialogueMessages: event.target.checked })
                                                    }
                                                />
                                            }
                                            label="Сообщения диалога"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.syncPipelineStages}
                                                    onChange={(event) =>
                                                        updateForm({ syncPipelineStages: event.target.checked })
                                                    }
                                                />
                                            }
                                            label="Этапы воронки"
                                        />
                                    </Stack>
                                </Box>

                                {selectedConnection && (
                                    <>
                                        <Divider />
                                        <Box>
                                            <Typography sx={{ fontWeight: 900, mb: 1.25 }}>
                                                Статус синхронизации
                                            </Typography>
                                            <Box
                                                display="grid"
                                                gridTemplateColumns={{
                                                    xs: 'repeat(2, minmax(0, 1fr))',
                                                    md: 'repeat(6, minmax(0, 1fr))',
                                                }}
                                                gap={1.5}
                                            >
                                                {[
                                                    ['Всего', selectedConnection.stats.total],
                                                    ['В CRM', selectedConnection.stats.synced],
                                                    ['Ошибки', selectedConnection.stats.errors],
                                                    ['В работе', selectedConnection.stats.inWork],
                                                    ['Успешно', selectedConnection.stats.won],
                                                    ['Закрыто', selectedConnection.stats.lost],
                                                ].map(([label, value]) => (
                                                    <Box key={label}>
                                                        <Typography sx={{ fontSize: 22, fontWeight: 900 }}>
                                                            {value}
                                                        </Typography>
                                                        <Typography sx={{ color: 'rgba(0,0,0,0.52)', fontSize: 13 }}>
                                                            {label}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                            {(selectedConnection.lastCheckedAt || selectedConnection.lastSyncAt) && (
                                                <Typography sx={{ color: 'rgba(0,0,0,0.48)', mt: 1.25, fontSize: 14 }}>
                                                    {selectedConnection.lastCheckedAt
                                                        ? `Проверка: ${formatDateTime(
                                                              selectedConnection.lastCheckedAt
                                                          )}`
                                                        : ''}
                                                    {selectedConnection.lastCheckedAt && selectedConnection.lastSyncAt
                                                        ? ' · '
                                                        : ''}
                                                    {selectedConnection.lastSyncAt
                                                        ? `Синхронизация: ${formatDateTime(
                                                              selectedConnection.lastSyncAt
                                                          )}`
                                                        : ''}
                                                </Typography>
                                            )}
                                        </Box>
                                    </>
                                )}

                                {selectedConnection && (
                                    <>
                                        <Divider />
                                        <Box>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                gap={1}
                                                mb={1}
                                            >
                                                <Typography sx={{ fontWeight: 900 }}>Последние отправки</Typography>
                                                {bindingsLoading && <CircularProgress size={18} />}
                                            </Box>
                                            {bindings.length ? (
                                                <Stack spacing={0}>
                                                    {bindings.slice(0, 8).map((binding) => (
                                                        <Box
                                                            key={binding.id}
                                                            display="grid"
                                                            gridTemplateColumns={{
                                                                xs: '1fr',
                                                                md: 'minmax(0, 1fr) 170px',
                                                            }}
                                                            gap={1}
                                                            py={1}
                                                            borderTop={`1px solid ${BORDER}`}
                                                        >
                                                            <Box minWidth={0}>
                                                                <Typography
                                                                    sx={{
                                                                        fontWeight: 850,
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {binding.leadCategory || 'Лид'}{' '}
                                                                    {binding.remoteDealId
                                                                        ? `#${binding.remoteDealId}`
                                                                        : ''}
                                                                </Typography>
                                                                <Typography
                                                                    sx={{
                                                                        color: 'rgba(0,0,0,0.52)',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {binding.leadMessage ||
                                                                        binding.leadAuthor ||
                                                                        'Без текста'}
                                                                </Typography>
                                                            </Box>
                                                            <Box textAlign={{ xs: 'left', md: 'right' }}>
                                                                <Typography sx={{ fontWeight: 850 }}>
                                                                    {getBindingStatusLabel(binding.syncStatus)}
                                                                </Typography>
                                                                {formatDateTime(binding.lastSyncedAt) && (
                                                                    <Typography
                                                                        sx={{ color: 'rgba(0,0,0,0.46)', fontSize: 13 }}
                                                                    >
                                                                        {formatDateTime(binding.lastSyncedAt)}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            ) : (
                                                <Typography sx={{ color: 'rgba(0,0,0,0.48)' }}>
                                                    Отправок в CRM пока нет
                                                </Typography>
                                            )}
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </Paper>
                    </Box>
                )}
            </Stack>
        </DesktopLayout>
    );
}
